import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Lock, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ReasonDialog } from '@/components/accountant/reason-dialog';
import { Combobox, type ComboItem } from '@/components/combobox';
import { DateInput } from '@/components/date-input';
import { Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DrillLink } from '@/features/reports/report-table';
import { fyStart } from '@/features/reports/settings';
import {
  movableAccount,
  selectionTotal,
  useReclassifyLines,
  type ReclassifyFilters,
  type ReclassifyLine,
  type ReclassifyResult,
} from '@/lib/accountant';
import { useAccounts, useTaxCodes } from '@/lib/accounts';
import { api } from '@/lib/api';
import { ltr, nameFor } from '@/lib/arabic';
import { useMe } from '@/lib/clients';
import { useContacts } from '@/lib/contacts';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDate } from '@/lib/format';
import { useLanguage, useT } from '@/lib/i18n';
import { sourceLabel } from '@/lib/journal';
import { formatMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { useCompanySettings } from '@/lib/settings';
import { homeCurrency } from '@/lib/org-locale';

export const Route = createFileRoute('/accounting/reclassify')({ component: Reclassify });

const TYPES = [
  ['', 'accountingPages.reclassifyPage.types.all'],
  ['manual', 'accountingPages.reclassifyPage.types.manual'],
  ['invoice', 'accountingPages.reclassifyPage.types.invoice'],
  ['credit_note', 'accountingPages.reclassifyPage.types.creditNote'],
  ['sales_receipt', 'accountingPages.reclassifyPage.types.salesReceipt'],
  ['bill', 'accountingPages.reclassifyPage.types.bill'],
  ['debit_note', 'accountingPages.reclassifyPage.types.debitNote'],
  ['expense', 'accountingPages.reclassifyPage.types.expense'],
  ['bank', 'accountingPages.reclassifyPage.types.bank'],
  ['opening', 'accountingPages.reclassifyPage.types.opening'],
] as const;

/** Screen A8 (document 06): Reclassify transactions, QBO Accountant tools. */
function Reclassify() {
  const t = useT();
  const lang = useLanguage();
  const orgId = useOrgId();
  const me = useMe();
  const qc = useQueryClient();
  const company = useCompanySettings();
  const accounts = useAccounts();
  const taxCodes = useTaxCodes();
  const contacts = useContacts();
  const today = todayIso();
  const fy = fyStart(today, company.data?.advanced.fiscalYearStart ?? 1);
  const [draft, setDraft] = useState<ReclassifyFilters>({
    from: fy,
    to: today,
    accountTypes: 'all',
  });
  const [filters, setFilters] = useState<ReclassifyFilters>(draft);
  const lines = useReclassifyLines(filters);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [toAccount, setToAccount] = useState<string | null>(null);
  const [toTax, setToTax] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const isStaff = me.data?.isStaff ?? false;

  const accountItems: ComboItem[] = useMemo(
    () =>
      (accounts.data ?? []).filter(movableAccount).map((a) => ({
        id: a.id,
        label: `${a.code} ${nameFor(lang, a.fullName, a.fullNameAr)}`,
        keywords: `${a.accountTypeName} ${a.fullName} ${a.fullNameAr ?? ''}`,
        group: nameFor(lang, a.accountTypeName, a.accountTypeNameAr),
      })),
    [accounts.data, lang],
  );
  const accountNames = useMemo(
    () => new Map((accounts.data ?? []).map((a) => [a.id, nameFor(lang, a.name, a.nameAr)])),
    [accounts.data, lang],
  );
  const contactItems: ComboItem[] = useMemo(
    () =>
      (contacts.data ?? []).map((c) => ({
        id: c.id,
        label: nameFor(lang, c.displayName, c.displayNameAr),
        keywords: `${c.displayName} ${c.displayNameAr ?? ''}`,
      })),
    [contacts.data, lang],
  );
  const zeroCodes = (taxCodes.data ?? []).filter(
    (t) => t.isActive && Number(t.rate) === 0 && t.kind !== 'reverse_charge',
  );

  const rows = lines.data ?? [];
  const selectable = rows.filter((l) => !l.blocked);
  const chosen = rows.filter((l) => selected.has(l.lineId));
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allOn = selectable.length > 0 && selectable.every((l) => selected.has(l.lineId));

  const apply = useMutation({
    mutationFn: (reason: string) =>
      api<ReclassifyResult>(`/orgs/${orgId}/reclassify`, {
        method: 'POST',
        body: JSON.stringify({
          lineIds: [...selected],
          ...(toAccount ? { toAccountId: toAccount } : {}),
          ...(toTax ? { toTaxCodeId: toTax } : {}),
          reason,
        }),
      }),
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      setSelected(new Set());
      setConfirming(false);
      const first = r.entries[0];
      toast(
        t('accountingPages.reclassifyPage.toastDone', {
          lines: t('accountingPages.reclassifyPage.lineCount', { count: r.lines }),
          transactions: t('accountingPages.reclassifyPage.transactionCount', {
            count: r.entries.length,
          }),
        }),
        first
          ? {
              link: {
                to: `/accounting/journal-entries/${first.entryId}`,
                label: t('accountingPages.reclassifyPage.view'),
              },
            }
          : {},
      );
    },
  });

  if (!orgId) return <EmptyState title={t('shell.selectClient')} />;
  const target = accountItems.find((a) => a.id === toAccount)?.label;
  const taxLabel =
    toTax === 'none'
      ? t('accountingPages.reclassifyPage.noVatCodeLower')
      : (zeroCodes.find((t) => t.id === toTax)?.code ?? '');

  return (
    <div>
      <PageHeader
        title={t('accountingPages.reclassifyPage.title')}
        description={t('accountingPages.reclassifyPage.description')}
      />
      <form
        className="mb-3 grid grid-cols-2 gap-3 rounded-lg border bg-card p-4 md:grid-cols-4 xl:grid-cols-7"
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.from && draft.to && draft.from > draft.to) {
            setRangeError(t('accountingPages.reclassifyPage.rangeError'));
            return;
          }
          setRangeError(null);
          setSelected(new Set());
          setFilters(draft);
        }}
      >
        <Field label={t('accountingPages.reclassifyPage.account')} className="xl:col-span-2">
          {() => (
            <Combobox
              ariaLabel={t('accountingPages.reclassifyPage.account')}
              items={accountItems}
              value={draft.accounts?.[0] ?? null}
              placeholder={t('accountingPages.reclassifyPage.allAccounts')}
              onChange={(id) => setDraft({ ...draft, accounts: id ? [id] : [] })}
            />
          )}
        </Field>
        <Field label={t('accountingPages.reclassifyPage.accountTypes')}>
          {(id) => (
            <NativeSelect
              id={id}
              value={draft.accountTypes ?? 'all'}
              onChange={(e) =>
                setDraft({ ...draft, accountTypes: e.target.value as 'all' | 'pl' | 'bs' })
              }
            >
              <option value="all">{t('accountingPages.reclassifyPage.all')}</option>
              <option value="pl">{t('accountingPages.reclassifyPage.profitAndLoss')}</option>
              <option value="bs">{t('accountingPages.reclassifyPage.balanceSheet')}</option>
            </NativeSelect>
          )}
        </Field>
        <Field label={t('accountingPages.reclassifyPage.from')}>
          {(id) => (
            <DateInput
              id={id}
              value={draft.from ?? ''}
              onChange={(v) => setDraft({ ...draft, from: v })}
            />
          )}
        </Field>
        <Field label={t('accountingPages.reclassifyPage.to')} error={rangeError ?? undefined}>
          {(id) => (
            <DateInput
              id={id}
              value={draft.to ?? ''}
              onChange={(v) => setDraft({ ...draft, to: v })}
            />
          )}
        </Field>
        <Field label={t('accountingPages.reclassifyPage.name')}>
          {() => (
            <Combobox
              ariaLabel={t('accountingPages.reclassifyPage.name')}
              items={contactItems}
              value={draft.contactId ?? null}
              placeholder={t('accountingPages.reclassifyPage.allNames')}
              onChange={(id) =>
                setDraft({ ...draft, ...(id ? { contactId: id } : { contactId: '' }) })
              }
            />
          )}
        </Field>
        <Field label={t('accountingPages.reclassifyPage.transactionType')}>
          {(id) => (
            <NativeSelect
              id={id}
              value={draft.types?.[0] ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, types: e.target.value ? [e.target.value] : [] })
              }
            >
              {TYPES.map(([v, l]) => (
                <option key={v} value={v}>
                  {t(l)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <div className="col-span-2 flex items-end gap-2 md:col-span-4 xl:col-span-7">
          <div className="relative w-80">
            <Search
              className="absolute top-2.5 start-2.5 size-4 text-muted-foreground"
              aria-hidden
            />
            <Input
              className="ps-8"
              placeholder={t('accountingPages.reclassifyPage.searchPlaceholder')}
              aria-label={t('accountingPages.reclassifyPage.search')}
              maxLength={100}
              value={draft.search ?? ''}
              onChange={(e) => setDraft({ ...draft, search: e.target.value })}
            />
          </div>
          <Button type="submit">{t('accountingPages.reclassifyPage.find')}</Button>
        </div>
      </form>

      {isStaff && selected.size ? (
        <div
          className="sticky top-0 z-10 mb-3 flex flex-wrap items-end gap-3 rounded-lg border border-primary/30 bg-accent px-4 py-3"
          data-testid="reclassify-bar"
        >
          <p className="me-2 text-sm">
            <span className="font-medium">
              {t('accountingPages.reclassifyPage.selected', { count: selected.size })}
            </span>
            <span className="tnum ms-2 text-muted-foreground">
              {t('accountingPages.reclassifyPage.net', {
                amount: `${homeCurrency()} ${formatMinor(selectionTotal(rows, selected))}`,
              })}
            </span>
          </p>
          <Field label={t('accountingPages.reclassifyPage.changeAccountTo')} className="w-72">
            {() => (
              <Combobox
                ariaLabel={t('accountingPages.reclassifyPage.changeAccountTo')}
                items={accountItems}
                value={toAccount}
                placeholder={t('accountingPages.reclassifyPage.keepAccount')}
                onChange={setToAccount}
              />
            )}
          </Field>
          <Field label={t('accountingPages.reclassifyPage.changeVatTo')} className="w-56">
            {(id) => (
              <NativeSelect id={id} value={toTax} onChange={(e) => setToTax(e.target.value)}>
                <option value="">{t('accountingPages.reclassifyPage.keepVat')}</option>
                <option value="none">{t('accountingPages.reclassifyPage.noVatCode')}</option>
                {zeroCodes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.code} – {nameFor(lang, t.name, t.nameAr)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Button disabled={!toAccount && !toTax} onClick={() => setConfirming(true)}>
            {t('accountingPages.reclassifyPage.reclassify')}
          </Button>
          <Button variant="ghost" onClick={() => setSelected(new Set())}>
            {t('accountingPages.reclassifyPage.clearSelection')}
          </Button>
        </div>
      ) : null}

      {lines.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : lines.isError ? (
        <EmptyState
          title={t('accountingPages.reclassifyPage.loadError')}
          hint={lines.error.message}
        />
      ) : !rows.length ? (
        <EmptyState
          title={t('accountingPages.reclassifyPage.noLines')}
          hint={t('accountingPages.reclassifyPage.noLinesHint')}
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  {isStaff ? (
                    <input
                      type="checkbox"
                      aria-label={t('accountingPages.reclassifyPage.selectAll')}
                      className="size-4 accent-primary"
                      checked={allOn}
                      onChange={() =>
                        setSelected(allOn ? new Set() : new Set(selectable.map((l) => l.lineId)))
                      }
                    />
                  ) : null}
                </TableHead>
                <TableHead>{t('accountingPages.reclassifyPage.colDate')}</TableHead>
                <TableHead>{t('accountingPages.reclassifyPage.colType')}</TableHead>
                <TableHead>{t('accountingPages.reclassifyPage.colNo')}</TableHead>
                <TableHead>{t('accountingPages.reclassifyPage.colName')}</TableHead>
                <TableHead>{t('accountingPages.reclassifyPage.colMemo')}</TableHead>
                <TableHead>{t('accountingPages.reclassifyPage.colAccount')}</TableHead>
                <TableHead>{t('accountingPages.reclassifyPage.colVat')}</TableHead>
                <TableHead className="text-end">
                  {t('accountingPages.reclassifyPage.colAmount')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((l) => (
                <LineRow
                  key={l.lineId}
                  line={l}
                  accountName={accountNames.get(l.accountId) ?? l.accountName}
                  staff={isStaff}
                  checked={selected.has(l.lineId)}
                  onToggle={() => toggle(l.lineId)}
                />
              ))}
            </TableBody>
          </Table>
          {rows.length >= 300 ? (
            <p className="border-t px-4 py-2 text-xs text-muted-foreground">
              {t('accountingPages.reclassifyPage.latest300')}
            </p>
          ) : null}
        </div>
      )}

      <ReasonDialog
        open={confirming}
        title={t('accountingPages.reclassifyPage.confirmTitle', { count: chosen.length })}
        description={
          <>
            {target ? (
              <>{t('accountingPages.reclassifyPage.moveTo', { account: target })} </>
            ) : null}
            {toTax ? (
              <>{t('accountingPages.reclassifyPage.vatBecomes', { code: taxLabel })} </>
            ) : null}
            {t('accountingPages.reclassifyPage.confirmBody')}
          </>
        }
        confirmLabel={t('accountingPages.reclassifyPage.reclassify')}
        pending={apply.isPending}
        error={apply.error?.message ?? null}
        onConfirm={(reason) => apply.mutate(reason)}
        onClose={() => {
          apply.reset();
          setConfirming(false);
        }}
      />
    </div>
  );
}

function LineRow({
  line: l,
  accountName,
  staff,
  checked,
  onToggle,
}: {
  line: ReclassifyLine;
  accountName: string;
  staff: boolean;
  checked: boolean;
  onToggle: () => void;
}) {
  const t = useT();
  const drill = {
    kind: 'entry' as const,
    id: l.entryId,
    ...(l.sourceId && l.sourceType !== 'manual'
      ? { source: { type: l.sourceType, id: l.sourceId } }
      : {}),
  };
  return (
    <TableRow data-testid={`line-${l.lineId}`} className={l.blocked ? 'text-muted-foreground' : ''}>
      <TableCell>
        {l.blocked ? (
          <span
            title={l.blocked}
            aria-label={t('accountingPages.reclassifyPage.lockedAria', { reason: l.blocked })}
          >
            <Lock className="size-4" />
          </span>
        ) : staff ? (
          <input
            type="checkbox"
            className="size-4 accent-primary"
            aria-label={t('accountingPages.reclassifyPage.selectLine', {
              line: l.lineNo,
              document: ltr(
                l.documentNumber ??
                  t('accountingPages.reclassifyPage.journalNo', { no: l.entryNo }),
              ),
            })}
            checked={checked}
            onChange={onToggle}
          />
        ) : null}
      </TableCell>
      <TableCell>{formatDate(l.date)}</TableCell>
      <TableCell>{sourceLabel(l.sourceType)}</TableCell>
      <TableCell>
        <DrillLink drill={drill}>{l.documentNumber ?? l.entryNo}</DrillLink>
      </TableCell>
      <TableCell>{l.contactName ?? ''}</TableCell>
      <TableCell className="max-w-[280px] truncate" title={l.description ?? l.memo ?? ''}>
        {l.description ?? l.memo ?? ''}
        {l.blocked ? <span className="block text-xs">{l.blocked}</span> : null}
      </TableCell>
      <TableCell>
        {l.accountCode} {accountName}
      </TableCell>
      <TableCell>{l.taxCode ?? ''}</TableCell>
      <TableCell className="tnum text-end">{formatAmount(l.amount)}</TableCell>
    </TableRow>
  );
}
